import { comboRules, contentConfig, master, songsByAlbum } from "../data";
import { PERSONALITY_COPY_LOCK, type PersonalityId } from "../data/personality-copy-lock";
import { sortedDimensions, sumDimensions } from "./dimensions";
import type {
  Classification,
  ComboRule,
  ExpandedSong,
  InternalDimension,
  OfficialResult,
  PersonaId,
  Song,
} from "../types";

const TYPE_DIMENSIONS = ["相信", "不服", "眷恋", "远方"] as const;

function toPersonaId(internalId: `类型${string}`): PersonaId {
  return `T${internalId.slice(2).padStart(2, "0")}`;
}

export function classifySelection(rawScores: Record<InternalDimension, number>): Classification {
  const percentiles = Object.fromEntries(
    (Object.keys(contentConfig.normalization.percentile_lookup) as InternalDimension[]).map((dimension) => {
      const lookup = contentConfig.normalization.percentile_lookup[dimension];
      const raw = String(rawScores[dimension]);
      const percentile = lookup[raw];
      if (percentile === undefined) throw new Error(`无法换算 ${dimension} 的分数 ${raw}`);
      return [dimension, percentile];
    }),
  ) as Record<InternalDimension, number>;

  const persona = contentConfig.types.find((candidate) =>
    TYPE_DIMENSIONS.every((dimension) => candidate.conditions[dimension] === (percentiles[dimension] >= 50 ? "高" : "低")),
  );
  if (!persona) throw new Error("九首选择未能对应到唯一人格");

  const coreDimensions = [...TYPE_DIMENSIONS].sort(
    (left, right) => Math.abs(percentiles[right] - 50) - Math.abs(percentiles[left] - 50),
  );
  return { personaId: toPersonaId(persona.internal_id), percentiles, coreDimensions };
}

export function matchComboRule(selectedSongs: Song[]): ComboRule | undefined {
  const counts = new Map<string, number>();
  for (const song of selectedSongs) {
    for (const tag of song.combo_tags) counts.set(tag, (counts.get(tag) ?? 0) + 1);
  }
  return comboRules.find((rule) =>
    Object.entries(rule.when).every(([tag, threshold]) => (counts.get(tag) ?? 0) >= threshold),
  );
}

function albumDelta(song: Song, coreDimensions: InternalDimension[]) {
  const albumSongs = songsByAlbum.get(song.album) ?? [];
  if (!albumSongs.length) return 0;
  const total = coreDimensions.reduce((sum, dimension) => {
    const mean = albumSongs.reduce((value, item) => value + item.weights[dimension], 0) / albumSongs.length;
    return sum + Math.abs(song.weights[dimension] - mean) / 3;
  }, 0);
  return Math.min(1, total / Math.max(1, coreDimensions.length));
}

const themeFrequency = new Map<string, number>();
for (const song of master.songs) {
  for (const theme of song.themes) themeFrequency.set(theme, (themeFrequency.get(theme) ?? 0) + 1);
}
const maxThemeFrequency = Math.max(...themeFrequency.values());

function themeRarity(song: Song) {
  const sum = song.themes.reduce((value, theme) => value + (1 - ((themeFrequency.get(theme) ?? 1) - 1) / maxThemeFrequency), 0);
  return sum / song.themes.length;
}

function chooseBranch(
  song: Song,
  topDimensions: InternalDimension[],
  coreDimensions: InternalDimension[],
  usedCopies: Set<string>,
) {
  const candidates = Object.entries(song.branches) as [`${InternalDimension}高`, string][];
  const priority = [...new Set([...topDimensions, ...coreDimensions])];
  candidates.sort((left, right) => {
    const leftDimension = left[0].slice(0, -1) as InternalDimension;
    const rightDimension = right[0].slice(0, -1) as InternalDimension;
    const leftRank = priority.indexOf(leftDimension);
    const rightRank = priority.indexOf(rightDimension);
    return (leftRank < 0 ? 99 : leftRank) - (rightRank < 0 ? 99 : rightRank);
  });
  const selected = candidates.find(([, copy]) => !usedCopies.has(copy)) ?? candidates[0];
  if (!selected) return {};
  usedCopies.add(selected[1]);
  return { branchKey: selected[0], branchCopy: selected[1] };
}

export function rankExpandedSongs(
  selectedSongs: Song[],
  classification: Classification,
  count = 3,
): ExpandedSong[] {
  const rawScores = sumDimensions(selectedSongs);
  const topDimensions = sortedDimensions(rawScores).slice(0, 2);
  const scored = selectedSongs.map((song, originalIndex) => {
    const personalityAffinity = song.affinity.includes(classification.personaId) ? 1 : 0;
    const topDimensionHit = topDimensions.some((dimension) => `${dimension}高` in song.branches) ? 1 : 0;
    const explainScore =
      0.4 * personalityAffinity +
      0.3 * albumDelta(song, classification.coreDimensions) +
      0.2 * topDimensionHit +
      0.1 * themeRarity(song);
    return { song, explainScore, originalIndex };
  });
  scored.sort((left, right) => right.explainScore - left.explainScore || left.originalIndex - right.originalIndex);
  const usedCopies = new Set<string>();
  return scored.slice(0, count).map(({ song, explainScore }) => ({
    song,
    explainScore,
    ...chooseBranch(song, topDimensions, classification.coreDimensions, usedCopies),
  }));
}

export function buildOfficialResult(selectedSongs: Song[]): OfficialResult {
  const rawScores = sumDimensions(selectedSongs);
  const classification = classifySelection(rawScores);
  const lockedPersona = PERSONALITY_COPY_LOCK[classification.personaId as PersonalityId];
  if (!lockedPersona) throw new Error("人格内容未能与判型结果对应");
  const persona = {
    title: lockedPersona.name,
    judgement: lockedPersona.tagline,
    body: lockedPersona.body,
  };
  return {
    classification,
    persona,
    selectedSongs,
    expandedSongs: rankExpandedSongs(selectedSongs, classification, 3),
    comboRule: matchComboRule(selectedSongs),
  };
}
